import { useState } from "react";
import { CALCOLATORE as C } from "@/config/site";
import { goToForm } from "@/lib/prefill";

const eur = (n) => (Number.isFinite(n) ? `${String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")} €` : "—");
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const num = (v) => (v === "" ? NaN : Number(v));

const Field = ({ id, label, children, hint }) => (
  <div>
    <label htmlFor={id} className="label">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-ink/55">{hint}</p>}
  </div>
);

const Slider = ({ id, value, onChange, testId }) => (
  <div className="flex items-center gap-4">
    <input id={id} type="range" min="0" max="100" step="1" value={value} onChange={(e) => onChange(Number(e.target.value))}
           className="w-full h-2 cursor-pointer accent-forest" data-testid={testId} />
    <span className="w-14 text-right font-serif text-xl text-forest tabular-nums" data-testid={`${testId}-value`}>{value}%</span>
  </div>
);

export const Calcolatore = () => {
  const [camere, setCamere] = useState("8");
  const [prezzo, setPrezzo] = useState("100");
  const [occ, setOcc] = useState(50);
  const [occOb, setOccOb] = useState(null);
  const [prezzoOb, setPrezzoOb] = useState(null);

  const nCam = clamp(num(camere), 1, 200);
  const nPrezzo = clamp(num(prezzo), 20, 2000);
  const vOccOb = occOb ?? occ;
  const vPrezzoOb = prezzoOb ?? prezzo;
  const nPrezzoOb = clamp(num(vPrezzoOb), 20, 2000);

  const fattOggi = nCam * nPrezzo * (occ / 100) * 365;
  const fattOb = nCam * nPrezzoOb * (vOccOb / 100) * 365;
  const divario = fattOb - fattOggi;
  const revparOggi = nPrezzo * (occ / 100);
  const revparOb = nPrezzoOb * (vOccOb / 100);
  const valid = Number.isFinite(fattOggi) && Number.isFinite(fattOb);
  const hasGap = valid && divario > 0;

  const cta = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "calculator_used", camere: nCam, divario: hasGap ? Math.round(divario) : 0 });
    const base = `Dal calcolatore: ${nCam} camere, prezzo medio ${Math.round(nPrezzo)} €, occupazione ${occ}%. Obiettivo: occupazione ${vOccOb}%, prezzo ${Math.round(nPrezzoOb)} €.`;
    goToForm({
      situazione: "Ho già una struttura",
      obiettivi: ["Aumentare il fatturato"],
      numero_camere: String(nCam),
      fatturato_annuo: eur(fattOggi),
      fatturato_obiettivo: String(Math.round(fattOb)),
      messaggio: hasGap ? `${base} Divario stimato: ${eur(divario)}/anno.` : base,
      overwrite: true,
    });
  };

  return (
    <section id="calcolatore" className="bg-ivory py-20 lg:py-28" data-testid="calcolatore-section">
      <div className="container-ph">
        <div className="reveal max-w-2xl">
          <span className="gold-line mb-5" />
          <p className="eyebrow">{C.eyebrow}</p>
          <h2 className="h-section mt-3">{C.title}</h2>
          <p className="mt-5 text-base sm:text-lg text-ink/75 leading-relaxed">{C.intro}</p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          <div className="reveal bg-white border border-ivory-dark p-7 space-y-6" data-testid="calc-card-oggi">
            <h3 className="font-serif text-2xl text-forest">{C.oggiTitle}</h3>
            <Field id="calc-camere" label={C.labels.camere}>
              <input id="calc-camere" type="number" min="1" max="200" inputMode="numeric" className="field" value={camere} onChange={(e) => setCamere(e.target.value)} data-testid="calc-input-camere" />
            </Field>
            <Field id="calc-prezzo" label={C.labels.prezzo}>
              <input id="calc-prezzo" type="number" min="20" max="2000" inputMode="numeric" className="field" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} data-testid="calc-input-prezzo" />
            </Field>
            <Field id="calc-occ" label={C.labels.occupazione}>
              <Slider id="calc-occ" value={occ} onChange={setOcc} testId="calc-slider-occ" />
            </Field>
          </div>

          <div className="reveal reveal-delay-1 bg-white border border-ivory-dark p-7 space-y-6" data-testid="calc-card-obiettivi">
            <h3 className="font-serif text-2xl text-forest">{C.obiettiviTitle}</h3>
            <Field id="calc-occ-ob" label={C.labels.occObiettivo}>
              <Slider id="calc-occ-ob" value={vOccOb} onChange={setOccOb} testId="calc-slider-occ-ob" />
            </Field>
            <Field id="calc-prezzo-ob" label={C.labels.prezzoObiettivo}>
              <input id="calc-prezzo-ob" type="number" min="20" max="2000" inputMode="numeric" className="field" value={vPrezzoOb} onChange={(e) => setPrezzoOb(e.target.value)} data-testid="calc-input-prezzo-ob" />
            </Field>
          </div>

          <div className="reveal reveal-delay-2 bg-forest text-ivory p-7 flex flex-col" data-testid="calc-results">
            <p className="eyebrow">{C.labels.fattOggi}</p>
            <p className="mt-1 font-serif text-3xl tabular-nums" data-testid="calc-fatt-oggi">{valid ? eur(fattOggi) : "—"}</p>
            <p className="eyebrow mt-6">{C.labels.fattObiettivo}</p>
            <p className="mt-1 font-serif text-3xl tabular-nums" data-testid="calc-fatt-obiettivo">{valid ? eur(fattOb) : "—"}</p>
            <div className="mt-6 border-t border-ivory/15 pt-6">
              <p className="eyebrow">{C.labels.divario}</p>
              {hasGap ? (
                <>
                  <p className="mt-1 font-serif text-4xl text-bronze tabular-nums" data-testid="calc-divario">{eur(divario)}</p>
                  <p className="mt-1 text-sm text-ivory/70" data-testid="calc-divario-mese">{eur(divario / 12)} {C.labels.alMese}</p>
                </>
              ) : (
                <p className="mt-2 text-sm text-ivory/75" data-testid="calc-no-gap">{valid ? C.noGap : "—"}</p>
              )}
            </div>
            <div className="mt-6 border-t border-ivory/15 pt-5 text-sm">
              <p className="text-ivory/60 text-xs">{C.labels.revpar}</p>
              <p className="mt-1.5 flex justify-between tabular-nums"><span className="text-ivory/70">{C.labels.revparOggi}</span><span data-testid="calc-revpar-oggi">{valid ? eur(revparOggi) : "—"}</span></p>
              <p className="mt-1 flex justify-between tabular-nums"><span className="text-ivory/70">{C.labels.revparObiettivo}</span><span data-testid="calc-revpar-obiettivo">{valid ? eur(revparOb) : "—"}</span></p>
            </div>
            <button onClick={cta} disabled={!valid} className="btn-primary !bg-ivory !text-forest hover:!bg-white mt-8 w-full disabled:opacity-60" data-testid="calc-cta-button">{C.button}</button>
          </div>
        </div>
        <p className="reveal mt-6 text-xs text-ink/50 max-w-3xl" data-testid="calc-disclaimer">{C.disclaimer}</p>
      </div>
    </section>
  );
};
